// cookingTimeLimits.js — Single source of truth for cooking-time caps.
//
// These caps are shared by:
//   - the meal-plan generation prompt (src/utils/promptGenerators.js), which
//     TELLS the model these exact per-mealType/complexity limits, and
//   - the runtime validator (src/components/MealPlan/hooks/useMealPlanLoader.js),
//     which REJECTS generations whose cookingTime exceeds the cap.
//
// CRITICAL: the validator must never be stricter than the numbers the prompt
// gives the model. If they diverge, the model produces a value that is valid
// per the prompt but rejected by the validator → the whole generation retries
// and eventually hard-fails with an error screen. Keep them identical by
// importing from this module on both sides.
//
// PASSIVE-TIME MODEL: a recipe's cookingTime is TOTAL time (active + passive).
// The base caps below are the ACTIVE-work guidance; COOKING_TIME_PASSIVE_BONUS
// is added on top for unattended oven/simmer time. Prompt, validator and clamp
// must all use base+bonus as the real total ceiling — keep them in lockstep.

// Canonical caps (minutes) by meal type and complexity tier.
export const COOKING_TIME_LIMITS = {
  breakfast: { simple: 15, standard: 20, advanced: 25 },
  lunch: { simple: 25, standard: 35, advanced: 45 },
  dinner: { simple: 35, standard: 45, advanced: 60 },
};

// Default complexity tier when cookingStyle is missing for a meal type.
export const DEFAULT_COOKING_COMPLEXITY = 'standard';

// Fallback caps when a meal type can't be resolved to the table above.
const FALLBACK_LIMITS = { breakfast: 20, lunch: 35, dinner: 45 };

// Tolerance (minutes) the runtime validator allows ABOVE the stated cap before
// flagging a numeric violation. LLMs routinely overshoot a stated cap by a
// couple of minutes; hard-failing an otherwise-good 9-recipe set over +2 min is
// bad UX. The cap the model is told stays exact — only the validator is lenient.
// This tolerance applies ONLY to the numeric cookingTime check, never to the
// overnight / slow-breakfast concept bans.
export const COOKING_TIME_TOLERANCE_MIN = 5;

const normalizeMealType = (value) => String(value || '').trim().toLowerCase();

const parseMinutes = (value) => {
  if (value === null || value === undefined) return null;
  const match = String(value).match(/\d+/);
  if (!match) return null;
  const minutes = Number(match[0]);
  return Number.isFinite(minutes) ? minutes : null;
};

/**
 * Resolve the hard cooking-time cap (minutes) for a meal type + complexity.
 * Returns null when the meal type is unknown (no cap should be enforced).
 */
export const getCookingTimeLimit = (mealType, complexity = DEFAULT_COOKING_COMPLEXITY) => {
  const type = normalizeMealType(mealType);
  const tier = COOKING_TIME_LIMITS[type];
  if (!tier) return null;
  return tier[complexity] || tier[DEFAULT_COOKING_COMPLEXITY] || FALLBACK_LIMITS[type] || null;
};

// Passive-cooking bonus (minutes) allowed ON TOP OF the active cap. Unattended
// oven / roast / bake / braise / simmer time may extend the TOTAL dish time
// beyond the active cap without adding active work, so genuinely easy oven dishes
// (e.g. a casserole baking raw potatoes ~40 min) are no longer forced into a too-
// short window. Breakfast gets no bonus — it must stay quick. Applied in lockstep
// by the meal-plan prompt, this module's clampCookingTime, the recipe-details
// prompt, the alternative-recipe prompt, and the runtime validator.
export const COOKING_TIME_PASSIVE_BONUS = { breakfast: 0, lunch: 30, dinner: 30 };

/** Passive-time bonus (minutes) for a meal type. 0 when unknown. */
export const getPassiveCookingBonus = (mealType) => {
  const type = normalizeMealType(mealType);
  return COOKING_TIME_PASSIVE_BONUS[type] || 0;
};

/**
 * Effective MAXIMUM total cooking time (minutes) = active cap + passive bonus.
 * This is the real ceiling for a recipe's total cookingTime; the base
 * getCookingTimeLimit() value remains the ACTIVE-work guidance.
 */
export const getEffectiveCookingTimeLimit = (mealType, complexity = DEFAULT_COOKING_COMPLEXITY) => {
  const base = getCookingTimeLimit(mealType, complexity);
  if (base == null) return null;
  return base + getPassiveCookingBonus(mealType);
};

/**
 * Clamp a details-stage cooking_time against the meal's cap.
 *
 * Returns the details value when it parses and stays within cap + tolerance;
 * otherwise falls back to the already-validated initial cooking time (or the
 * cap itself when no fallback is available). This prevents the recipe-details
 * model from overwriting a validated cookingTime with a larger out-of-range
 * value — without triggering a full regeneration.
 */
export const clampCookingTime = (detailsTime, fallbackTime, mealType, complexity = DEFAULT_COOKING_COMPLEXITY) => {
  const parsed = parseMinutes(detailsTime);
  if (parsed === null) return fallbackTime ?? detailsTime ?? null;

  const limit = getEffectiveCookingTimeLimit(mealType, complexity);
  if (!limit) return detailsTime;

  if (parsed <= limit + COOKING_TIME_TOLERANCE_MIN) return detailsTime;
  return fallbackTime ?? `${limit}`;
};
